import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptySearchResult,
  IndexTable,
  Icon,
  Pagination,
  TextField,
  useBreakpoints,
} from "@shopify/polaris";
import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { SearchMinor } from "@shopify/polaris-icons";
import DateRangePicker from "../../../components/datepicker/DateRangePicker";
import {
  getAdvanceSearchData,
  getDashboardAllData,
  getFailedData,
  getMissingData,
  getPendingData,
  getSyncedData,
} from "../../../redux/store/dashboard/Action";
import {
  formatStoreDateTime,
  mapStoreStatusToBadgeStatus,
  ORDER_TABS,
  toOrdersRequestModel,
  toOrdersViewModel,
} from "../mappers";
import StorePageFrame from "./StorePageFrame";

const statusLabel = (status) => {
  switch ((status || "").toUpperCase()) {
    case "SUCCESS":
      return "Success";
    case "IN_QUEUE":
    case "QUEUED":
      return "In Queue";
    case "MISSING_INFO":
      return "Missing Info";
    case "PROCESSING":
    case "PENDING":
      return "Processing";
    default:
      return "Failed";
  }
};

const nonBlank = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || null;
};

export const shopifyOrderAdminUrl = (order) => {
  const domain = nonBlank(order?.myShopifyDomain)?.toLowerCase();
  const storeOrderId = nonBlank(order?.storeOrderId);
  if (
    !domain ||
    !storeOrderId ||
    !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(domain)
  ) {
    return null;
  }
  return `https://${domain}/admin/orders/${encodeURIComponent(storeOrderId)}`;
};

export const paypalTransactionUrl = (transactionId) => {
  const normalized = nonBlank(transactionId);
  return normalized
    ? `https://www.paypal.com/activity/payment/${encodeURIComponent(normalized)}`
    : null;
};

export const trackingDetailsUrl = (trackingNumber) => {
  const normalized = nonBlank(trackingNumber);
  return normalized
    ? `https://www.trackingmore.com/en/${encodeURIComponent(normalized)}`
    : null;
};

const OrdersLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const breakpoints = useBreakpoints();

  const ordersState = useSelector((store) => store.requiredStoreDashboardData);
  const viewModel = toOrdersViewModel(ordersState, id);

  const queryFromUrl = (searchParams.get("query") || "").trim();
  const [queryValue, setQueryValue] = useState(queryFromUrl);
  const [selectedTab, setSelectedTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const resolveTabIndex = useCallback((value) => {
    if (typeof value === "number" && ORDER_TABS[value]) {
      return value;
    }
    if (typeof value === "string") {
      const byId = ORDER_TABS.findIndex((tab) => tab.id === value);
      if (byId >= 0) {
        return byId;
      }
      const parsed = Number(value);
      if (!Number.isNaN(parsed) && ORDER_TABS[parsed]) {
        return parsed;
      }
    }
    return 0;
  }, []);

  const currentStatus = ORDER_TABS[resolveTabIndex(selectedTab)]?.status || null;

  const fetchOrders = useCallback(
    ({
      page = currentPage,
      status = currentStatus,
      search = queryFromUrl,
      startDate = selectedDateRange.startDate,
      endDate = selectedDateRange.endDate,
    } = {}) => {
      dispatch(
        getAdvanceSearchData(
          toOrdersRequestModel({
            page,
            status,
            search,
            startDate,
            endDate,
            shopId: id,
          })
        )
      );
    },
    [currentPage, currentStatus, dispatch, id, queryFromUrl, selectedDateRange.endDate, selectedDateRange.startDate]
  );

  useEffect(() => {
    // Prevent stale date filters from another store or page affecting this view.
    sessionStorage.removeItem("startDate");
    sessionStorage.removeItem("endDate");
    dispatch(getDashboardAllData(id));
    dispatch(getMissingData(id));
    dispatch(getPendingData(id));
    dispatch(getFailedData(id));
    dispatch(getSyncedData(id));
  }, [dispatch, id]);

  useEffect(() => {
    // The query parameter is the durable deep-link contract used by disputes
    // and by browser back/forward navigation.
    setQueryValue(queryFromUrl);
    setSelectedTab(0);
    setCurrentPage(0);
    setSelectedDateRange({ startDate: null, endDate: null });
    sessionStorage.removeItem("startDate");
    sessionStorage.removeItem("endDate");
    dispatch(
      getAdvanceSearchData(
        toOrdersRequestModel({
          page: 0,
          status: null,
          search: queryFromUrl,
          startDate: null,
          endDate: null,
          shopId: id,
        })
      )
    );
  }, [dispatch, id, queryFromUrl]);

  useEffect(() => {
    if (viewModel.isAuthenticationError) {
      navigate("/logout");
    }
  }, [viewModel.isAuthenticationError, navigate]);

  const tabs = useMemo(
    () =>
      ORDER_TABS.map((tab, index) => ({
        id: tab.id,
        index,
        content: `${tab.label} ${viewModel.counts[tab.countKey].toLocaleString("en-US")}`,
        onAction: () => {},
      })),
    [viewModel.counts]
  );

  const hasNext = currentPage < Math.max(viewModel.pageCount, 1) - 1;
  const hasPrevious = currentPage > 0;
  const selectedTabIndex = resolveTabIndex(selectedTab);

  const updateQueryUrl = useCallback(
    (nextQuery) => {
      const normalized = String(nextQuery || "").trim();
      const nextParams = new URLSearchParams(searchParams);
      if (normalized) {
        nextParams.set("query", normalized);
      } else {
        nextParams.delete("query");
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const submitSearch = useCallback(() => {
    const normalized = queryValue.trim();
    setCurrentPage(0);
    if (normalized === queryFromUrl) {
      fetchOrders({ page: 0, search: normalized });
      return;
    }
    updateQueryUrl(normalized);
  }, [fetchOrders, queryFromUrl, queryValue, updateQueryUrl]);

  const clearFilters = useCallback(() => {
    setQueryValue("");
    setSelectedTab(0);
    setCurrentPage(0);
    setSelectedDateRange({ startDate: null, endDate: null });
    sessionStorage.removeItem("startDate");
    sessionStorage.removeItem("endDate");
    if (queryFromUrl) {
      updateQueryUrl("");
    } else {
      fetchOrders({
        page: 0,
        status: null,
        search: "",
        startDate: null,
        endDate: null,
      });
    }
  }, [fetchOrders, queryFromUrl, updateQueryUrl]);

  return (
    <StorePageFrame title="Orders" gap="12px">
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
        {viewModel.error ? (
          <Banner
            title="Orders could not be refreshed"
            status="critical"
            action={{ content: "Retry", onAction: () => fetchOrders() }}
          >
            <p>
              {viewModel.error}
              {viewModel.rows.length
                ? " Showing the last successful order result."
                : " Order availability is unknown until this request succeeds."}
            </p>
          </Banner>
        ) : null}
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <DateRangePicker
            handleOrdersFilter={(ordersStatus, _search, startDate, endDate) => {
              const resolvedStartDate = startDate || null;
              const resolvedEndDate = endDate || null;
              setSelectedDateRange({
                startDate: resolvedStartDate,
                endDate: resolvedEndDate,
              });
              setCurrentPage(0);
              fetchOrders({
                page: 0,
                status: ordersStatus || currentStatus,
                search: queryFromUrl,
                startDate: resolvedStartDate,
                endDate: resolvedEndDate,
              });
            }}
            resetDate={false}
          />
        </div>

        <Card>
          <div
            style={{
              padding: "10px 16px",
              borderBottom: "1px solid #d1d5db",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: "4px", alignItems: "center", flexWrap: "wrap" }}>
              {tabs.map((tab) => {
                const isSelected = selectedTabIndex === tab.index;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setSelectedTab(tab.index);
                      setCurrentPage(0);
                      fetchOrders({ page: 0, status: ORDER_TABS[tab.index]?.status || null });
                    }}
                    style={{
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 14px",
                      background: isSelected ? "#d4ebe3" : "transparent",
                      color: "#3f4a5a",
                      fontSize: "13px",
                      fontWeight: 500,
                      lineHeight: "20px",
                      cursor: "pointer",
                    }}
                  >
                    {tab.content}
                  </button>
                );
              })}
            </div>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch();
              }}
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                minWidth: "min(100%, 360px)",
              }}
            >
              <div style={{ flex: 1, minWidth: "220px" }}>
                <TextField
                  label="Search orders"
                  labelHidden
                  value={queryValue}
                  onChange={setQueryValue}
                  placeholder="Order or transaction ID"
                  autoComplete="off"
                  prefix={<Icon source={SearchMinor} tone="base" />}
                  clearButton={Boolean(queryValue)}
                  onClearButtonClick={() => setQueryValue("")}
                />
              </div>
              <Button submit>Search</Button>
              <Button onClick={clearFilters}>Clear filters</Button>
            </form>
          </div>

          <IndexTable
            condensed={breakpoints.smDown}
            resourceName={{ singular: "order", plural: "orders" }}
            itemCount={viewModel.rows.length}
            selectedItemsCount={0}
            onSelectionChange={() => {}}
            selectable={false}
            loading={viewModel.isLoading}
            headings={[
              { title: "" },
              { title: "Order" },
              { title: "Transaction" },
              { title: "Delivered" },
              { title: "Date" },
            ]}
          >
            {viewModel.rows.length === 0 && viewModel.isLoading
              ? null
              : viewModel.rows.length === 0 && !viewModel.error ? (
              <IndexTable.Row id="empty" position={0}>
                <IndexTable.Cell colSpan={5}>
                  <div style={{ padding: "22px", minHeight: "260px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <EmptySearchResult
                      title="No orders found"
                      description="Try changing the filters or search term"
                      withIllustration
                    />
                  </div>
                </IndexTable.Cell>
              </IndexTable.Row>
            ) : viewModel.rows.length === 0 ? (
              <IndexTable.Row id="unavailable" position={0}>
                <IndexTable.Cell colSpan={5}>
                  <div style={{ padding: "22px" }}>
                    Order data is unavailable. Retry the request before treating
                    this store as having no orders.
                  </div>
                </IndexTable.Cell>
              </IndexTable.Row>
            ) : (
              viewModel.rows.map((order, index) => {
                const orderUrl = shopifyOrderAdminUrl(order);
                const transactionUrl = paypalTransactionUrl(order?.transactionId);
                const trackingUrl = trackingDetailsUrl(order?.trackingNumber);
                const orderLabel = order?.storeOrderId ? `#${order.storeOrderId}` : "N/A";
                const transactionLabel = order?.transactionId || "N/A";
                const carrierLabel =
                  order?.paypalCarrier && order?.paypalCarrier !== "OTHER"
                    ? order.paypalCarrier
                    : order?.carrier || "N/A";

                return (
                  <IndexTable.Row
                    id={String(order?.fulfillmentId || index)}
                    key={String(order?.fulfillmentId || index)}
                    position={index}
                  >
                    <IndexTable.Cell>
                      <Badge status={mapStoreStatusToBadgeStatus(order?.orderStatus)}>
                        {statusLabel(order?.orderStatus)}
                      </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {orderUrl ? (
                        <a
                          style={{ color: "#315db0", textDecoration: "underline" }}
                          href={orderUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {orderLabel}
                        </a>
                      ) : orderLabel}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {transactionUrl ? (
                        <a
                          style={{ color: "#315db0", textDecoration: "underline" }}
                          href={transactionUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {transactionLabel}
                        </a>
                      ) : transactionLabel}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {trackingUrl ? (
                        <a
                          style={{ color: "#315db0", textDecoration: "underline" }}
                          href={trackingUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {carrierLabel}
                        </a>
                      ) : carrierLabel}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {formatStoreDateTime(order?.createdAt || order?.processedAt) || "N/A"}
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })
            )}
          </IndexTable>

          <div style={{ padding: "12px 16px", display: "flex", justifyContent: "center", borderTop: "1px solid #d1d5db" }}>
            <Pagination
              label={`${currentPage + 1} / ${Math.max(viewModel.pageCount, 1)}`}
              hasPrevious={hasPrevious}
              onPrevious={() => {
                if (!hasPrevious) return;
                const nextPage = currentPage - 1;
                setCurrentPage(nextPage);
                fetchOrders({ page: nextPage });
              }}
              hasNext={hasNext}
              onNext={() => {
                if (!hasNext) return;
                const nextPage = currentPage + 1;
                setCurrentPage(nextPage);
                fetchOrders({ page: nextPage });
              }}
            />
          </div>
        </Card>
      </div>
    </StorePageFrame>
  );
};

export default OrdersLayout;
